/**
 * Types para o módulo Customer Support - Frontend
 */

// Fases da implantação (novo fluxo)
export enum ImplementationPhase {
  KICK_OFF = 'KICK_OFF',
  INTEGRATION = 'INTEGRATION',
  BUSINESS_RULES = 'BUSINESS_RULES',
  INFRASTRUCTURE = 'INFRASTRUCTURE',
  TEMPLATES = 'TEMPLATES',
  CHATCONNECT = 'CHATCONNECT',
  CHATBOT = 'CHATBOT',
  GO_LIVE = 'GO_LIVE',
  POST_IMPLEMENTATION = 'POST_IMPLEMENTATION',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

// Status da implantação
export enum ImplementationStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  BLOCKED = 'BLOCKED',
  IN_DEVELOPMENT = 'IN_DEVELOPMENT',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

// Labels das fases
export const PHASE_LABELS: Record<ImplementationPhase, string> = {
  [ImplementationPhase.KICK_OFF]: 'Kick-off',
  [ImplementationPhase.INTEGRATION]: 'Integração',
  [ImplementationPhase.BUSINESS_RULES]: 'Regras de Negócio',
  [ImplementationPhase.INFRASTRUCTURE]: 'Infraestrutura Meta',
  [ImplementationPhase.TEMPLATES]: 'Templates',
  [ImplementationPhase.CHATCONNECT]: 'ChatConnect',
  [ImplementationPhase.CHATBOT]: 'Chatbot',
  [ImplementationPhase.GO_LIVE]: 'Go-Live',
  [ImplementationPhase.POST_IMPLEMENTATION]: 'Pós-Implantação',
  [ImplementationPhase.COMPLETED]: 'Concluído',
  [ImplementationPhase.CANCELLED]: 'Cancelado'
};

// Cores das fases
export const PHASE_COLORS: Record<ImplementationPhase, string> = {
  [ImplementationPhase.KICK_OFF]: '#94a3b8',
  [ImplementationPhase.INTEGRATION]: '#60a5fa',
  [ImplementationPhase.BUSINESS_RULES]: '#8b5cf6',
  [ImplementationPhase.INFRASTRUCTURE]: '#f59e0b',
  [ImplementationPhase.TEMPLATES]: '#a855f7',
  [ImplementationPhase.CHATCONNECT]: '#3b82f6',
  [ImplementationPhase.CHATBOT]: '#10b981',
  [ImplementationPhase.GO_LIVE]: '#22c55e',
  [ImplementationPhase.POST_IMPLEMENTATION]: '#6366f1',
  [ImplementationPhase.COMPLETED]: '#22c55e',
  [ImplementationPhase.CANCELLED]: '#ef4444'
};

// Labels dos status
export const STATUS_LABELS: Record<ImplementationStatus, string> = {
  [ImplementationStatus.IN_PROGRESS]: 'Em Andamento',
  [ImplementationStatus.BLOCKED]: 'Bloqueado',
  [ImplementationStatus.IN_DEVELOPMENT]: 'Em Desenvolvimento',
  [ImplementationStatus.COMPLETED]: 'Concluído',
  [ImplementationStatus.CANCELLED]: 'Cancelado'
};

// Interfaces
export interface Department {
  name: string;
}

export interface Operator {
  name: string;
  department: string;
}

export interface ImplementationContact {
  type: 'new' | 'existing';
  value: string;
}

export interface Client {
  id: number;
  legal_name: string;
  trade_name?: string;
  company_tax_id: string;
  email?: string;
  phone_number?: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
}

export interface ImplementationObservation {
  id: number;
  implementation_id: number;
  user_id: number;
  content: string;
  phase: ImplementationPhase | null;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  user?: User;
}

export interface ChatConnectSchedule {
  id: number;
  implementation_id: number;
  user_id?: number;
  department_id?: number;
  user_name?: string;
  scheduled_at: string;
  notes?: string;
  reminder_email?: string;
  send_reminder_30min: boolean;
  reminder_2h_sent: boolean;
  reminder_30min_sent: boolean;
  reminder_at_time_sent: boolean;
  created_at: string;
  updated_at: string;
  user?: User;
}

export interface PostImplementationCall {
  id: number;
  implementation_id: number;
  scheduled_at: string;
  call_type: 'FEEDBACK' | 'FOLLOW_UP' | 'SUPPORT';
  notes?: string;
  has_issues: boolean;
  issues_description?: string;
  status: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface ClientImplementation {
  id: number;
  client_id: number;
  
  // ETAPA 1: KICK-OFF
  decision_maker_whatsapp?: string;
  send_weekly_roi_report: boolean;
  
  // ETAPA 2: INTEGRAÇÃO E SISTEMA
  hiring_reason?: string;
  tool_expectation?: string;
  departments: Department[];
  operators: Operator[];
  integration_with_portal?: boolean;
  integration_with_platform?: boolean;
  training_with?: 'manager' | 'attendant' | null;
  training_type?: 'initial' | 'review' | null;
  training_scheduled_at?: string | null;
  
  // ETAPA 3: REGRAS DE NEGÓCIO
  management_system?: 'hinova' | 'south' | 'other' | null;
  hinova_system_link?: string | null;
  hinova_user_atomos?: string | null;
  hinova_api_token?: string | null;
  south_token?: string | null;
  south_base_url?: string | null;
  other_system_notes?: string | null;
  contacts?: ImplementationContact[];
  send_welcome_how?: string;
  due_dates?: string;
  reminder_how?: string;
  billing_how?: string;
  grace_period_post_due?: string;
  billing_frequency_post_due?: string;
  send_birthday_message: boolean;
  send_payment_confirmation: boolean;
  
  // ETAPA 4: INFRAESTRUTURA META
  has_bm?: boolean | null; // null = não respondido, true = sim, false = não
  bm_verified?: boolean | null; // null = não respondido, true = sim, false = não
  atomos_added_partner: boolean;
  has_facebook_profile: boolean;
  bm_company_form_filled: boolean;
  bm_site_added: boolean;
  bm_sms_code_sent: boolean;
  bm_phone_for_sms?: string;
  infrastructure_notes?: string;
  
  // ETAPA 5: TEMPLATES
  template_welcome?: string;
  template_reminder?: string;
  template_billing?: string;
  template_inspection?: string;
  template_birthday?: string;
  template_others?: string;
  
  // ETAPA 6: CHATCONNECT
  chatconnect_departments_created: Department[];
  chatconnect_users_created: Operator[];
  chatconnect_user_passwords: Record<string, string>;
  
  // ETAPA 7: CHATBOT
  chatbot_ai_instructions?: string;
  chatbot_compliance_done: boolean;
  chatbot_second_copy_working: boolean;
  chatbot_faq_added: boolean;
  chatbot_tests_done: boolean;
  
  // ETAPA 8: GO-LIVE
  go_live_validated_with_decisor: boolean;
  go_live_validated_with_team: boolean;
  next_feedback_meeting_date?: string;
  
  // Status e Fase
  current_phase: ImplementationPhase;
  status: ImplementationStatus;
  
  // Metadados
  completed_at?: string;
  
  // Timestamps por etapa
  step1_completed_at?: string;
  step2_completed_at?: string;
  step3_completed_at?: string;
  step4_completed_at?: string;
  step5_completed_at?: string;
  step6_completed_at?: string;
  step7_completed_at?: string;
  step8_completed_at?: string;
  
  // Métricas de tempo (em horas)
  step1_duration_hours?: number | null;
  step2_duration_hours?: number | null;
  step3_duration_hours?: number | null;
  step4_duration_hours?: number | null;
  step5_duration_hours?: number | null;
  step6_duration_hours?: number | null;
  step7_duration_hours?: number | null;
  step8_duration_hours?: number | null;
  total_duration_hours?: number | null;
  
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  client?: Client;
  createdBy?: User;
  updatedBy?: User;
  observations?: ImplementationObservation[];
  chatconnectSchedules?: ChatConnectSchedule[];
  postImplementationCalls?: PostImplementationCall[];
}

export interface CreateImplementationDTO {
  client_id: number;
  decision_maker_whatsapp?: string;
  send_weekly_roi_report?: boolean;
  hiring_reason?: string;
  tool_expectation?: string;
  departments?: Department[];
  operators?: Operator[];
  integration_with_portal?: boolean;
  integration_with_platform?: boolean;
  training_with?: 'manager' | 'attendant' | null;
  training_type?: 'initial' | 'review' | null;
  training_scheduled_at?: string | null;
  management_system?: 'hinova' | 'south' | 'other' | null;
  hinova_system_link?: string | null;
  hinova_user_atomos?: string | null;
  hinova_api_token?: string | null;
  south_token?: string | null;
  south_base_url?: string | null;
  other_system_notes?: string | null;
  contacts?: ImplementationContact[];
  send_welcome_how?: string;
  due_dates?: string;
  reminder_how?: string;
  billing_how?: string;
  grace_period_post_due?: string;
  billing_frequency_post_due?: string;
  send_birthday_message?: boolean;
  send_payment_confirmation?: boolean;
  has_bm?: boolean;
  bm_verified?: boolean;
  atomos_added_partner?: boolean;
  has_facebook_profile?: boolean;
  bm_company_form_filled?: boolean;
  bm_site_added?: boolean;
  bm_sms_code_sent?: boolean;
  bm_phone_for_sms?: string;
  infrastructure_notes?: string;
  template_welcome?: string;
  template_reminder?: string;
  template_billing?: string;
  template_inspection?: string;
  template_birthday?: string;
  template_others?: string;
  chatconnect_departments_created?: Department[];
  chatconnect_users_created?: Operator[];
  chatconnect_user_passwords?: Record<string, string>;
  chatbot_ai_instructions?: string;
  chatbot_compliance_done?: boolean;
  chatbot_second_copy_working?: boolean;
  chatbot_faq_added?: boolean;
  chatbot_tests_done?: boolean;
  go_live_validated_with_decisor?: boolean;
  go_live_validated_with_team?: boolean;
  next_feedback_meeting_date?: string;
  current_phase?: ImplementationPhase;
  status?: ImplementationStatus;
}

export interface UpdateImplementationDTO extends Partial<CreateImplementationDTO> {
  current_phase?: ImplementationPhase;
  status?: ImplementationStatus;
}

export interface CreateObservationDTO {
  content: string;
  phase?: ImplementationPhase | null;
  is_pinned?: boolean;
}

export interface UpdateObservationDTO {
  content?: string;
  is_pinned?: boolean;
}

export interface CreateChatConnectScheduleDTO {
  implementation_id: number;
  user_id?: number;
  department_id?: number;
  user_name?: string;
  scheduled_at: string;
  notes?: string;
  reminder_email?: string;
  send_reminder_30min?: boolean;
}

export interface CreatePostImplementationCallDTO {
  implementation_id: number;
  scheduled_at: string;
  call_type: 'FEEDBACK' | 'FOLLOW_UP' | 'SUPPORT';
  notes?: string;
}

export interface UpdatePostImplementationCallDTO {
  notes?: string;
  has_issues?: boolean;
  issues_description?: string;
  status?: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';
}

export interface KanbanData {
  [key: string]: ClientImplementation[];
}

export interface ImplementationTraining {
  id: number;
  implementation_id: number;
  training_with: 'manager' | 'attendant';
  training_type: 'initial' | 'review';
  scheduled_at: string;
  notes?: string | null;
  created_by?: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateImplementationTrainingDTO {
  training_with: 'manager' | 'attendant';
  training_type: 'initial' | 'review';
  scheduled_at: string;
  notes?: string | null;
}

export interface KanbanColumn {
  id: number;
  key: string;
  label: string;
  color: string;
  order_index: number;
  is_built_in: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateKanbanColumnDTO {
  label: string;
  color?: string;
  key?: string;
}

export interface UpdateKanbanColumnDTO {
  label?: string;
  color?: string;
  is_active?: boolean;
}

// Business Rules (Regras de Negócio dinâmicas)
export interface BusinessRuleItem {
  client_product_id: number;
  product_name: string;
  rule_description: string | null;
  observations: string | null;
  is_checked: boolean;
}

export interface BusinessRulesAcceptance {
  id: number;
  implementation_id: number;
  token: string;
  pdf_s3_key: string | null;
  interview_date: string;
  accepted_at: string | null;
  accepted_ip: string | null;
  created_at: string;
}

export interface BusinessRulesAcceptancePublicData {
  acceptance: BusinessRulesAcceptance;
  client: {
    legal_name: string;
    trade_name: string | null;
    company_tax_id: string;
    email: string | null;
    phone_number: string | null;
  } | null;
  rules: Array<{
    product_name: string;
    rule_description: string | null;
  }>;
  interview_date: string;
}
